import React, { useEffect, useState, useRef } from 'react';
import PageExplainer from '@/components/PageExplainer';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MemberProfileModal from '@/components/MemberProfileModal';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { MessageCircle, Send, Users, PlusCircle, Search, X, ChevronDown, Smile, ThumbsUp, Pencil, Trash2, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import MemberAvatar from '../components/MemberAvatar';
import { toast } from 'sonner';
import Linkify from '@/lib/linkify';
import LinkPreviews from '@/components/LinkPreviews';
import ReactionBar from '@/components/ReactionBar';
import { REACTION_BY_TYPE } from '@/lib/reactions';

const EMOJI_LIST = ['😀','😂','😍','🙌','👍','👏','🔥','💡','✅','❤️','🎉','💪','🤝','👋','😊','🙏','⭐','💬','📌','🚀'];

const BACKEND_URL = '';

export default function MessagesPage({ user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewingMember, setViewingMember] = useState(null);
  const [messages, setMessages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', description: '', members: [] });
  const [activeTab, setActiveTab] = useState('direct');
  const messagesEndRef = useRef(null);
  const mobileThreadRef = useRef(null);
  const emojiRef = useRef(null); // kept to satisfy older callsites; picker
  // now uses shadcn Popover which handles its own positioning + outside-click.
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const isComposingRef = useRef(false);
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('session_token') : null;
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  useEffect(() => {
    loadMessages();
    loadGroups();
    loadUsers();
  }, [user]);

  useEffect(() => {
    const toUser = searchParams.get('to');
    if (toUser && users.length > 0) {
      const found = users.find(u => u.user_id === toUser);
      if (found) {
        setSelectedRecipient(toUser);
        setActiveTab('direct');
      }
    }
  }, [searchParams, users]);

  // Event-context: any message opened from an event's attendee directory /
  // profile modal carries an `?event_id=…` query. We stash the id + a short
  // title snapshot so outgoing messages inherit the tag and the composer
  // shows an "About: {event}" chip. Cleared when the recipient changes.
  const eventIdParam = searchParams.get('event_id');
  const [activeEventContext, setActiveEventContext] = useState(null);
  useEffect(() => {
    if (!eventIdParam) { setActiveEventContext(null); return; }
    // Pull the title once from the events list — cheap, cached by the browser
    fetch(`${BACKEND_URL}/api/events`, { credentials: 'include', headers })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const ev = (list || []).find(e => e.event_id === eventIdParam);
        setActiveEventContext(ev ? { event_id: ev.event_id, event_title: ev.title } : { event_id: eventIdParam, event_title: '' });
      })
      .catch(() => setActiveEventContext({ event_id: eventIdParam, event_title: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdParam]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, selectedRecipient]);

  // Scroll mobile thread into view when opened
  useEffect(() => {
    if (selectedRecipient && mobileThreadRef.current) {
      setTimeout(() => mobileThreadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [selectedRecipient]);

  // (Outside-click + Escape handling moved into shadcn's Popover, which
  // portals the picker to <body> so it can never be clipped by parent
  // overflow/z-index — fixes a long-running report where the picker
  // "appeared not to open" because it was hidden behind the cookie
  // banner or page chrome on small viewports.)

  const onEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
  };

  const loadMessages = () => {
    fetch(`${BACKEND_URL}/api/messages`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(setMessages)
      .catch(console.error);
  };

  const loadGroups = () => {
    fetch(`${BACKEND_URL}/api/groups`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(setGroups)
      .catch(console.error);
  };

  const loadUsers = () => {
    fetch(`${BACKEND_URL}/api/members`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(data => setUsers(data.filter(u => u.user_id !== user?.user_id)))
      .catch(console.error);
  };

  const handleSendDirect = async () => {
    if (sending) return;                                  // submit lock
    const content = newMessage.trim();
    if (!content || !selectedRecipient) return;

    const clientId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `c_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const tempMessage = {
      message_id: `temp_${clientId}`,
      client_message_id: clientId,
      sender_id: user?.user_id,
      sender_name: user?.name,
      recipient_ids: [selectedRecipient],
      content,
      chat_type: 'direct',
      event_id: activeEventContext?.event_id || null,
      event_title: activeEventContext?.event_title || null,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setSending(true);
    setNewMessage('');
    setMessages(prev => [...prev, tempMessage]);          // optimistic

    try {
      const response = await fetch(`${BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        credentials: 'include',
        body: JSON.stringify({
          recipient_ids: [selectedRecipient], content, chat_type: 'direct',
          client_message_id: clientId,
          event_id: activeEventContext?.event_id || undefined,
        })
      });
      if (!response.ok) throw new Error('Failed to send');
      const saved = await response.json();
      setMessages(prev => prev.map(m => m.message_id === tempMessage.message_id ? saved : m));
    } catch (error) {
      // Mark the optimistic message as failed instead of removing — gives the user clarity
      setMessages(prev => prev.map(m => m.message_id === tempMessage.message_id ? { ...m, _pending: false, _failed: true } : m));
      setNewMessage(content);                             // restore for retry
      toast.error('Failed to send message — restored your text');
    } finally {
      setSending(false);
    }
  };

  // Toggle a multi-emoji reaction on a single message. Optimistic update
  // for the picked emoji + roll back on failure.
  const handleReactMessage = async (msg, reactionType) => {
    if (!user?.user_id || !msg.message_id || msg.message_id.startsWith('temp_')) return;
    const prevMy = msg.my_reaction || null;
    const prevCounts = msg.reaction_counts || {};
    // Optimistic compute
    const nextCounts = { ...prevCounts };
    if (prevMy) nextCounts[prevMy] = Math.max(0, (nextCounts[prevMy] || 0) - 1);
    const nextMy = prevMy === reactionType ? null : reactionType;
    if (nextMy) nextCounts[nextMy] = (nextCounts[nextMy] || 0) + 1;
    setMessages(prev => prev.map(m => m.message_id === msg.message_id
      ? { ...m, my_reaction: nextMy, reaction_counts: nextCounts } : m));
    try {
      const res = await fetch(`${BACKEND_URL}/api/messages/${msg.message_id}/react`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ reaction: reactionType }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setMessages(prev => prev.map(m => m.message_id === msg.message_id
        ? { ...m, my_reaction: data.my_reaction, reaction_counts: data.reaction_counts } : m));
    } catch {
      // Roll back
      setMessages(prev => prev.map(m => m.message_id === msg.message_id
        ? { ...m, my_reaction: prevMy, reaction_counts: prevCounts } : m));
      toast.error('Could not update reaction');
    }
  };

  // Start inline-editing a message — sender only. Stash the original
  // text so cancel-without-save restores it cleanly.
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');

  const beginEditMessage = (msg) => {
    setEditingMessageId(msg.message_id);
    setEditingDraft(msg.content || '');
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingDraft('');
  };

  const handleSaveEdit = async (msg) => {
    const content = editingDraft.trim();
    if (!content) { toast.error('Message cannot be empty'); return; }
    if (content === msg.content) { cancelEditMessage(); return; }
    // Optimistic update
    setMessages(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, content, edited_at: new Date().toISOString() } : m));
    cancelEditMessage();
    try {
      const res = await fetch(`${BACKEND_URL}/api/messages/${msg.message_id}`, {
        method: 'PATCH', credentials: 'include', headers,
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      const saved = await res.json();
      setMessages(prev => prev.map(m => m.message_id === msg.message_id ? saved : m));
    } catch (e) {
      // Rollback
      setMessages(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, content: msg.content, edited_at: msg.edited_at } : m));
      toast.error(e.message || 'Could not edit message');
    }
  };

  const handleDeleteMessage = async (msg) => {
    if (!window.confirm('Delete this message? Recipients will see "(message deleted)" in its place.')) return;
    // Optimistic — replace content locally
    setMessages(prev => prev.map(m => m.message_id === msg.message_id
      ? { ...m, deleted_at: new Date().toISOString(), content: '', liked_by: [] } : m));
    try {
      const res = await fetch(`${BACKEND_URL}/api/messages/${msg.message_id}`, {
        method: 'DELETE', credentials: 'include', headers,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
    } catch (e) {
      // Rollback
      setMessages(prev => prev.map(m => m.message_id === msg.message_id ? msg : m));
      toast.error(e.message || 'Could not delete message');
    }
  };

  const handleSendGroup = async () => {
    if (!newMessage.trim() || !selectedGroup) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${selectedGroup}/messages?content=${encodeURIComponent(newMessage)}`, {
        method: 'POST',
        credentials: 'include',
        headers
      });
      if (!response.ok) throw new Error('Failed to send');
      toast.success('Message sent!');
      setNewMessage('');
      loadGroupMessages(selectedGroup);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const loadGroupMessages = async (groupId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/messages`, { credentials: 'include', headers });
      const data = await response.json();
      setMessages(data);
    } catch (error) {
      console.error('Failed to load group messages');
    }
  };

  const handleCreateGroup = async () => {
    if (!groupForm.name.trim() || groupForm.members.length === 0) {
      toast.error('Please provide group name and select members');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        credentials: 'include',
        body: JSON.stringify({ name: groupForm.name, description: groupForm.description, member_ids: groupForm.members })
      });
      if (!response.ok) throw new Error('Failed to create group');
      toast.success('Group created!');
      setShowGroupDialog(false);
      setGroupForm({ name: '', description: '', members: [] });
      loadGroups();
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const toggleMember = (userId) => {
    setGroupForm(prev => ({
      ...prev,
      members: prev.members.includes(userId) ? prev.members.filter(id => id !== userId) : [...prev.members, userId]
    }));
  };

  const selectGroup = (groupId) => {
    setSelectedGroup(groupId);
    setSelectedRecipient(null);
    loadGroupMessages(groupId);
  };

  const selectedUser = users.find(u => u.user_id === selectedRecipient);
  const selectedGroupData = groups.find(g => g.group_id === selectedGroup);

  // Compute per-contact unread counts and latest message time
  const contactMeta = {};
  messages.forEach(msg => {
    const isIncoming = msg.sender_id !== user?.user_id && msg.recipient_ids?.includes(user?.user_id);
    const partnerId = isIncoming ? msg.sender_id : (msg.recipient_ids || []).find(id => id !== user?.user_id);
    if (!partnerId) return;
    if (!contactMeta[partnerId]) contactMeta[partnerId] = { unread: 0, lastMessageTime: null };
    const msgTime = new Date(msg.created_at).getTime();
    if (!contactMeta[partnerId].lastMessageTime || msgTime > contactMeta[partnerId].lastMessageTime) {
      contactMeta[partnerId].lastMessageTime = msgTime;
    }
    if (isIncoming) {
      const readBy = msg.read_by || [];
      if (!readBy.includes(user?.user_id)) contactMeta[partnerId].unread++;
    }
  });

  const filteredUsers = users.filter(u => {
    if (!userSearch) return true;
    const s = userSearch.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.profile?.role?.toLowerCase().includes(s) || u.profile?.organisation?.toLowerCase().includes(s);
  }).sort((a, b) => {
    const metaA = contactMeta[a.user_id] || { unread: 0, lastMessageTime: 0 };
    const metaB = contactMeta[b.user_id] || { unread: 0, lastMessageTime: 0 };
    if (metaA.unread > 0 && metaB.unread === 0) return -1;
    if (metaB.unread > 0 && metaA.unread === 0) return 1;
    if (metaA.lastMessageTime && metaB.lastMessageTime) return metaB.lastMessageTime - metaA.lastMessageTime;
    if (metaA.lastMessageTime) return -1;
    if (metaB.lastMessageTime) return 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  const conversationMessages = (() => {
    // Three modes:
    //   1. Recipient chosen  → 1:1 thread with that person (optionally
    //      narrowed by event_id when an event context is active).
    //   2. Event context only → every event-tagged message this user is
    //      party to for that event, so the deep-link from the event
    //      card's "N new" chip lands the user directly in the event's
    //      combined thread without needing a recipient pre-selection.
    //   3. Group tab → all messages (existing behaviour).
    if (activeTab === 'group') {
      return [...messages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    let base;
    if (selectedRecipient) {
      base = messages.filter(m =>
        (m.sender_id === user?.user_id && m.recipient_ids?.includes(selectedRecipient)) ||
        (m.sender_id === selectedRecipient && m.recipient_ids?.includes(user?.user_id))
      );
    } else if (activeEventContext?.event_id) {
      base = messages.filter(m =>
        m.sender_id === user?.user_id || m.recipient_ids?.includes(user?.user_id)
      );
    } else {
      base = [];
    }
    return base
      .filter(m => !activeEventContext?.event_id || m.event_id === activeEventContext.event_id)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  })();

  const markConversationRead = (partnerId) => {
    fetch(`${BACKEND_URL}/api/messages/read-conversation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      credentials: 'include',
      body: JSON.stringify({ partner_id: partnerId })
    }).then(() => loadMessages()).catch(() => {});
  };

  const handleKeyDown = (e) => {
    // Don't send during IME composition (Chinese/Japanese keyboards)
    if (e.nativeEvent?.isComposing || isComposingRef.current) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (sending) return;
      activeTab === 'direct' ? handleSendDirect() : handleSendGroup();
    }
  };

  const getConvoForUser = (userId) => {
    return messages.filter(m =>
      (m.sender_id === user?.user_id && m.recipient_ids?.includes(userId)) ||
      (m.sender_id === userId && m.recipient_ids?.includes(user?.user_id))
    ).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  // Compact date+time formatter used on every message bubble. Shows
  // "Today · 14:32" / "Yesterday · 14:32" / "29 May · 14:32" /
  // "29 May 2025 · 14:32" depending on age so members can see both the
  // date and time at a glance without long repetitive strings.
  const formatMessageTimestamp = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    let datePart;
    if (diffDays === 0) datePart = 'Today';
    else if (diffDays === 1) datePart = 'Yesterday';
    else if (d.getFullYear() === now.getFullYear()) {
      datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } else {
      datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }
    return `${datePart} · ${time}`;
  };

  // Shared message bubble renderer
  const renderMessages = (msgs) => (
    msgs.length > 0 ? msgs.map(msg => {
      const isMine = msg.sender_id === user?.user_id;
      const reactionCounts = msg.reaction_counts || {};
      const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + (b || 0), 0);
      const isTemp = msg.message_id?.startsWith('temp_');
      const isDeleted = !!msg.deleted_at;
      const isEditing = editingMessageId === msg.message_id;
      return (
        <div key={msg.message_id} data-testid={`message-${msg.message_id}`}
          className={`flex group ${isMine ? 'justify-end' : 'justify-start'}`}>
          <div className="max-w-[80%] md:max-w-[70%] relative">
            <div className={`px-3 py-2 rounded-2xl ${
              isMine
                ? `${msg._failed ? 'bg-rose-500' : 'bg-brand-navy'} text-white rounded-br-md ${msg._pending ? 'opacity-70' : ''} ${isDeleted ? 'opacity-60' : ''}`
                : `bg-slate-100 text-slate-800 rounded-bl-md ${isDeleted ? 'opacity-60' : ''}`
            }`}>
              {activeTab === 'group' && !isMine && (
                <p className="text-xs font-semibold mb-0.5 opacity-70">{msg.sender_name}</p>
              )}
              {/* Event-context chip on the bubble — only shown when NOT
                  already filtering by event, so members can see which
                  event a message came from in a mixed inbox. */}
              {msg.event_id && !activeEventContext && (
                <button
                  type="button"
                  onClick={() => navigate(`/messages?to=${isMine ? msg.recipient_ids?.[0] : msg.sender_id}&event_id=${msg.event_id}`)}
                  data-testid={`message-event-pill-${msg.message_id}`}
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full mb-1 transition-colors ${
                    isMine
                      ? 'bg-white/15 text-white hover:bg-white/25 border border-white/20'
                      : 'bg-brand-sage/10 text-brand-sage hover:bg-brand-sage/20 border border-brand-sage/25'
                  }`}
                  title="Open this event's thread"
                >
                  <Users className="h-2.5 w-2.5" />
                  {msg.event_title || 'Event'}
                </button>
              )}
              {isDeleted ? (
                <p className={`text-sm italic ${isMine ? 'text-white/70' : 'text-slate-400'}`} data-testid={`message-deleted-${msg.message_id}`}>(message deleted)</p>
              ) : isEditing ? (
                <div className="space-y-2">
                  <Textarea
                    autoFocus
                    value={editingDraft}
                    onChange={(e) => setEditingDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') { e.preventDefault(); cancelEditMessage(); }
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSaveEdit(msg); }
                    }}
                    data-testid={`message-edit-input-${msg.message_id}`}
                    className={`text-sm min-h-[60px] resize-none ${isMine ? 'bg-white/10 border-white/30 text-white placeholder:text-white/50' : ''}`}
                  />
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={cancelEditMessage}
                      data-testid={`cancel-edit-${msg.message_id}`}
                      className={`h-7 px-2 text-xs ${isMine ? 'text-white/80 hover:bg-white/10 hover:text-white' : ''}`}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => handleSaveEdit(msg)}
                      data-testid={`save-edit-${msg.message_id}`}
                      className={`h-7 px-2 text-xs ${isMine ? 'bg-white text-brand-navy hover:bg-white/90' : 'bg-brand-navy text-white'}`}>
                      <Check className="h-3 w-3 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                    <Linkify text={msg.content} linkClassName={isMine ? 'text-white underline decoration-white/60 hover:decoration-white underline-offset-2 break-all font-medium' : ''} />
                  </p>
                  <LinkPreviews text={msg.content} />
                </>
              )}
              <p
                className={`text-[10px] mt-0.5 flex items-center gap-1 ${isMine ? 'text-white/70' : 'text-slate-400'}`}
                title={msg.created_at ? new Date(msg.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                data-testid={`message-timestamp-${msg.message_id}`}
              >
                {msg._pending && <span data-testid="msg-pending">Sending…</span>}
                {msg._failed && <span data-testid="msg-failed">⚠ Failed</span>}
                {!msg._pending && !msg._failed && formatMessageTimestamp(msg.created_at)}
                {msg.edited_at && !isDeleted && (
                  <span title={`Edited ${new Date(msg.edited_at).toLocaleString('en-GB')}`} className="italic">· edited</span>
                )}
              </p>
            </div>

            {/* Edit / Delete affordance — only on own non-deleted, non-temp messages. */}
            {isMine && !isTemp && !msg._failed && !isDeleted && !isEditing && (
              <div className="flex items-center justify-end gap-1 mt-1">
                <button type="button" onClick={() => beginEditMessage(msg)}
                  data-testid={`edit-message-${msg.message_id}`}
                  title="Edit message"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium text-slate-400 hover:text-brand-navy hover:bg-slate-50 transition-colors">
                  <Pencil className="h-3 w-3" />
                </button>
                <button type="button" onClick={() => handleDeleteMessage(msg)}
                  data-testid={`delete-message-${msg.message_id}`}
                  title="Delete message"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* React affordance — only on received messages. Always visible
                (no opacity-0 trick) so it works on touch devices too. */}
            {!isMine && !isTemp && !msg._failed && !isDeleted && (
              <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
                <ReactionBar
                  myReaction={msg.my_reaction}
                  counts={msg.reaction_counts}
                  onReact={(type) => handleReactMessage(msg, type)}
                  testIdPrefix={`message-${msg.message_id}`}
                  size="xs"
                />
              </div>
            )}
            {/* Show reaction tally on own messages too — read-only — so the
                sender can see when their message landed. */}
            {isMine && totalReactions > 0 && !isDeleted && (
              <div className="flex items-center justify-end mt-1">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-brand-blue/10 text-brand-navy"
                  data-testid={`message-reaction-count-${msg.message_id}`}>
                  {Object.entries(reactionCounts)
                    .filter(([, c]) => c > 0)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3)
                    .map(([type]) => <span key={type}>{REACTION_BY_TYPE[type]?.emoji}</span>)}
                  <span>{totalReactions}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }) : (
      <p className="text-center text-sm text-slate-400 py-4">No messages yet. Say hello!</p>
    )
  );

  // Shared input bar
  const renderInput = () => (
    <div className="space-y-2">
      {/* Event-context banner — appears whenever the user opened this thread
          from an event's attendee directory. Tapping ✕ drops the context
          so subsequent replies are plain 1:1 messages. */}
      {activeEventContext && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-brand-sage/10 border border-brand-sage/30 text-brand-sage text-xs" data-testid="message-event-context">
          <Users className="h-3.5 w-3.5" />
          <span className="flex-1 truncate">
            About <strong className="font-semibold">{activeEventContext.event_title || 'this event'}</strong>
          </span>
          <button
            type="button"
            onClick={() => {
              setActiveEventContext(null);
              // Also strip event_id from the URL so a reload doesn't reapply it
              const next = new URLSearchParams(searchParams);
              next.delete('event_id');
              navigate(`/messages?${next.toString()}`, { replace: true });
            }}
            className="text-brand-sage/70 hover:text-brand-sage"
            aria-label="Remove event context"
            data-testid="message-event-context-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="flex gap-2 relative">
        <div className="relative flex-1">
          <Textarea data-testid="message-input" placeholder={activeEventContext ? `Reply about ${activeEventContext.event_title || 'this event'}…` : 'Type your message...'}
            value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={handleKeyDown}
            className="min-h-[88px] max-h-40 resize-none text-sm border-slate-200 focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue pr-14"
            rows={3} />
          <div className="absolute right-3 bottom-3">
            <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
              <PopoverTrigger asChild>
                <button type="button"
                  className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="emoji-picker-toggle"
                  aria-label="Insert emoji">
                  <Smile className="h-5 w-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top" align="end" sideOffset={8} collisionPadding={12}
                className="w-64 p-2 z-[100]" data-testid="emoji-picker"
              >
                <div className="grid grid-cols-5 gap-1">
                  {EMOJI_LIST.map(em => (
                    <button key={em} type="button" onClick={() => onEmojiSelect(em)}
                      data-testid={`emoji-${em}`}
                      className="text-xl hover:bg-slate-100 rounded p-1.5 transition-colors">{em}</button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <Button data-testid="send-message-button" size="icon"
          onClick={activeTab === 'direct' ? handleSendDirect : handleSendGroup}
          disabled={sending || (activeTab === 'direct' && !selectedRecipient) || (activeTab === 'group' && !selectedGroup) || !newMessage.trim()}
          className="h-[88px] w-12 flex-shrink-0 bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white">
          <Send className={`h-5 w-5 ${sending ? 'animate-pulse' : ''}`} />
        </Button>
      </div>
    </div>
  );

  // Contact card renderer (shared between mobile/desktop)
  const renderContactCard = (u, isMobile = false) => {
    const meta = contactMeta[u.user_id] || { unread: 0 };
    const isSelected = selectedRecipient === u.user_id;
    return (
      <div key={u.user_id}>
        <button data-testid={`recipient-${u.user_id}`}
          onClick={() => {
            if (isMobile && isSelected) {
              setSelectedRecipient(null);
              setNewMessage('');
              return;
            }
            setSelectedRecipient(u.user_id);
            setSelectedGroup(null);
            if (meta.unread > 0) markConversationRead(u.user_id);
          }}
          className={`w-full p-3 text-left border-b border-slate-50 transition-colors ${
            isSelected ? 'bg-brand-navy/5 border-l-2 border-l-brand-navy' : 'hover:bg-slate-50'
          }`}>
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <MemberAvatar name={u.name} picture={`${BACKEND_URL}/api/members/${u.user_id}/avatar`} size={36} />
              {meta.unread > 0 && (
                <span data-testid={`unread-badge-${u.user_id}`} className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold px-1">
                  {meta.unread}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-sm truncate ${meta.unread > 0 ? 'font-bold text-slate-900' : 'font-medium'}`}>{u.name}</p>
              <p className="text-xs text-slate-500 truncate">{u.profile?.role || 'Member'}</p>
            </div>
            {isMobile && (
              <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${isSelected ? 'rotate-180' : ''}`} />
            )}
          </div>
        </button>

        {/* Mobile inline thread */}
        {isMobile && isSelected && (
          <div ref={mobileThreadRef} className="bg-slate-50 border-b border-slate-200" data-testid="mobile-message-thread">
            <div className="max-h-[50vh] overflow-y-auto px-3 py-3 space-y-2">
              {renderMessages(getConvoForUser(u.user_id))}
              <div ref={messagesEndRef} />
            </div>
            <div className="px-3 py-2 border-t border-slate-200 bg-white">
              {renderInput()}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl" style={{ height: 'calc(100vh - 6rem)' }}>
          {/* Header row */}
          <div className="flex items-center justify-between py-3 md:py-4 gap-2 flex-wrap">
            <h1 className="text-xl md:text-2xl font-bold font-heading gradient-text flex items-center gap-2">Messages <PageExplainer pageKey="messages" label="About Messages" /></h1>
            <div className="flex items-center gap-2">
              <Button data-testid="direct-messages-tab" size="sm"
                onClick={() => { setActiveTab('direct'); setSelectedGroup(null); loadMessages(); }}
                variant={activeTab === 'direct' ? 'default' : 'outline'}
                className={`text-xs md:text-sm ${activeTab === 'direct' ? 'bg-brand-navy text-white' : ''}`}>
                <MessageCircle className="h-3.5 w-3.5 mr-1" /> Direct
              </Button>
              <Button data-testid="group-chats-tab" size="sm"
                onClick={() => { setActiveTab('group'); setSelectedRecipient(null); }}
                variant={activeTab === 'group' ? 'default' : 'outline'}
                className={`text-xs md:text-sm ${activeTab === 'group' ? 'bg-brand-navy text-white' : ''}`}>
                <Users className="h-3.5 w-3.5 mr-1" /> Groups ({groups.length})
              </Button>
              <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="create-group-button" size="sm" className="bg-gradient-to-r from-brand-navy to-brand-sage text-white text-xs md:text-sm">
                    <PlusCircle className="h-3.5 w-3.5 mr-1" /> <span className="hidden sm:inline">New Group</span><span className="sm:hidden">New</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Create Group Chat</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Group Name</Label>
                      <Input data-testid="group-name-input" value={groupForm.name}
                        onChange={(e) => setGroupForm({...groupForm, name: e.target.value})} placeholder="e.g., Sustainability Leaders" />
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Textarea value={groupForm.description}
                        onChange={(e) => setGroupForm({...groupForm, description: e.target.value})} placeholder="Group purpose..." />
                    </div>
                    <div>
                      <Label>Select Members</Label>
                      <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-4">
                        {users.map(u => (
                          <div key={u.user_id} className="flex items-center space-x-2">
                            <Checkbox data-testid={`member-checkbox-${u.user_id}`}
                              checked={groupForm.members.includes(u.user_id)} onCheckedChange={() => toggleMember(u.user_id)} />
                            <label className="text-sm cursor-pointer">{u.name}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button data-testid="save-group-button" onClick={handleCreateGroup}
                      className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white">Create Group</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* ═══════ MOBILE LAYOUT ═══════ */}
          <div className="md:hidden pb-20" style={{ height: 'calc(100% - 4rem)' }}>
            <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
              {activeTab === 'direct' && (
                <div className="p-2.5 border-b border-slate-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input data-testid="mobile-user-search" placeholder="Search people..."
                      value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-9 h-9 text-sm border-slate-200" />
                  </div>
                </div>
              )}
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 14rem)' }}>
                {activeTab === 'direct' ? (
                  filteredUsers.length > 0 ? (
                    filteredUsers.map(u => renderContactCard(u, true))
                  ) : (
                    <div className="p-6 text-center text-sm text-slate-400">No contacts found</div>
                  )
                ) : (
                  groups.length > 0 ? groups.map(g => (
                    <div key={g.group_id}>
                      <button data-testid={`group-${g.group_id}`}
                        onClick={() => {
                          if (selectedGroup === g.group_id) { setSelectedGroup(null); return; }
                          selectGroup(g.group_id);
                        }}
                        className={`w-full p-3 text-left border-b border-slate-50 transition-colors ${
                          selectedGroup === g.group_id ? 'bg-brand-navy/5 border-l-2 border-l-brand-navy' : 'hover:bg-slate-50'
                        }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{g.name}</p>
                            <p className="text-xs text-slate-500">{g.members?.length} members</p>
                          </div>
                          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${selectedGroup === g.group_id ? 'rotate-180' : ''}`} />
                        </div>
                      </button>
                      {selectedGroup === g.group_id && (
                        <div className="bg-slate-50 border-b border-slate-200" data-testid="mobile-group-thread">
                          <div className="max-h-[50vh] overflow-y-auto px-3 py-3 space-y-2">
                            {renderMessages(conversationMessages)}
                            <div ref={messagesEndRef} />
                          </div>
                          <div className="px-3 py-2 border-t border-slate-200 bg-white">
                            {renderInput()}
                          </div>
                        </div>
                      )}
                    </div>
                  )) : (
                    <div className="p-6 text-center text-sm text-slate-400">No groups yet</div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* ═══════ DESKTOP LAYOUT ═══════ */}
          <div className="hidden md:flex gap-4" style={{ height: 'calc(100% - 4.5rem)' }}>
            {/* Left panel - scrollable user/group list */}
            <div className="w-72 flex-shrink-0 flex flex-col border border-slate-200 rounded-xl bg-white overflow-hidden">
              {activeTab === 'direct' && (
                <div className="p-3 border-b border-slate-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input data-testid="user-search-input" placeholder="Search people..."
                      value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-9 h-9 text-sm border-slate-200" />
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'direct' ? (
                  filteredUsers.map(u => renderContactCard(u, false))
                ) : (
                  groups.length > 0 ? groups.map(g => (
                    <button key={g.group_id} data-testid={`group-${g.group_id}`}
                      onClick={() => selectGroup(g.group_id)}
                      className={`w-full p-3 text-left border-b border-slate-50 transition-colors ${
                        selectedGroup === g.group_id ? 'bg-brand-navy/5 border-l-2 border-l-brand-navy' : 'hover:bg-slate-50'
                      }`}>
                      <p className="font-medium text-sm">{g.name}</p>
                      <p className="text-xs text-slate-500">{g.members?.length} members</p>
                    </button>
                  )) : (
                    <div className="p-6 text-center text-sm text-slate-400">No groups yet</div>
                  )
                )}
              </div>
            </div>

            {/* Right panel - sticky chat */}
            <div className="flex-1 flex flex-col border border-slate-200 rounded-xl bg-white overflow-hidden">
              {/* Chat header */}
              <div className="px-5 py-3 border-b border-slate-100 flex-shrink-0 bg-white">
                {activeTab === 'direct' && selectedUser ? (
                  <button
                    type="button"
                    onClick={() => setViewingMember(selectedUser)}
                    className="flex items-center gap-3 group rounded-lg -mx-2 px-2 py-1 hover:bg-slate-50 transition-colors"
                    data-testid="chat-header-profile-btn"
                    title="View profile"
                  >
                    <MemberAvatar
                      name={selectedUser.name}
                      picture={`${BACKEND_URL}/api/members/${selectedUser.user_id}/avatar`}
                      size={40}
                      ringClassName="ring-2 ring-transparent group-hover:ring-brand-navy/20 transition-all"
                    />
                    <div className="text-left">
                      <h3 className="font-semibold text-sm group-hover:text-brand-navy transition-colors">{selectedUser.name}</h3>
                      <p className="text-xs text-slate-500">{selectedUser.profile?.role || 'Member'}{selectedUser.profile?.organisation ? ` at ${selectedUser.profile.organisation}` : ''}</p>
                    </div>
                  </button>
                ) : activeTab === 'group' && selectedGroupData ? (
                  <div>
                    <h3 className="font-semibold text-sm">{selectedGroupData.name}</h3>
                    <p className="text-xs text-slate-500">{selectedGroupData.members?.length} members</p>
                  </div>
                ) : activeEventContext ? (
                  <div data-testid="chat-header-event">
                    <h3 className="font-semibold text-sm text-brand-sage flex items-center gap-1.5">
                      <Users className="h-4 w-4" /> {activeEventContext.event_title || 'Event thread'}
                    </h3>
                    <p className="text-xs text-slate-500">Every message tagged to this event</p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">{activeTab === 'direct' ? 'Select a person to start chatting' : 'Select a group to view messages'}</p>
                )}
              </div>

              {/* Messages area - scrollable */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {(selectedRecipient || selectedGroup || activeEventContext) ? (
                  <>
                    {renderMessages(conversationMessages)}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <MessageCircle className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                      <p className="text-sm text-slate-400">Select a conversation to get started</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Message input - sticky at bottom */}
              <div className="px-4 py-3 border-t border-slate-100 flex-shrink-0 bg-white">
                {renderInput()}
              </div>
            </div>
          </div>
        </div>
      </main>
      <MemberProfileModal
        member={viewingMember}
        open={!!viewingMember}
        onClose={() => setViewingMember(null)}
        user={user}
      />
    </div>
  );
}
